/**
 * The intake screen's polling and auto-draft decisions, extracted so they can
 * be pinned.
 *
 * These few booleans are the repair of 23 August 2026 (commit dd323f6): the
 * gate query used to be fetched once, at mount, when nothing had been read and
 * `can_draft` was false — and the auto-draft effect read exactly that
 * snapshot. When the read landed the effect re-ran against mount-time data,
 * found `can_draft` still false, and did nothing; the officer sat in front of
 * an empty confirm form until a window focus happened to refetch. Measured on
 * case 7477: 18.9 seconds of work behind 164 seconds of waiting, against 4.1
 * seconds repaired.
 *
 * The backend half of that repair is pinned (`test_intake_journey.py`,
 * `test_intake_rules.py`). The screen half was not pinned anywhere, which
 * means it could silently regress with the whole suite green. This module is
 * the screen half as pure functions, and `intake.test.ts` is the pin.
 *
 * Nothing here reads a clock or a query cache: timestamps and states are
 * passed in, so `NewCase.tsx` owns the wiring and this module owns the
 * decisions — the same split as `readPeriod()` one file over.
 */

import type { IntakeParticularView, StartIntakeBody } from '../api/client'

/** The read states after which a paper's read will not move again. */
const TERMINAL_READ_STATES = new Set(['REVIEW_READY', 'FAILED', 'CONFIRMED'])

/** One bound for every intake poll, so the two queries cannot drift apart. */
export const INTAKE_POLL_MS = 2500

/** What these decisions need from a paper row. A subset of `SourceFileView`. */
export interface ReadRow {
  ingestion_job_id?: string | null
  read_state?: string | null
  read_pages_done?: number | null
}

/** A read exists and has not finished — the screen must keep watching it. */
export function readStillRunning(row: ReadRow | null | undefined): boolean {
  if (!row?.ingestion_job_id) return false
  return !TERMINAL_READ_STATES.has(row.read_state ?? '')
}

/**
 * The read landed with something to show. `read_pages_done > 0` is part of the
 * landing, not a nicety: a read that reached REVIEW_READY with nothing read is
 * not a gate anybody should draft against.
 */
export function readLanded(row: ReadRow | null | undefined): boolean {
  if (!row) return false
  return (
    (row.read_state === 'REVIEW_READY' || row.read_state === 'CONFIRMED') &&
    (row.read_pages_done ?? 0) > 0
  )
}

/**
 * The papers list polls while any paper's read is still moving, and stops when
 * nothing is. A paper with no job at all — one the pipeline cannot open — is
 * not waited on: its read is never coming, and a poll that waits for it would
 * spin for ever under a list that already says the paper is unread.
 */
export function papersPollInterval(rows: readonly ReadRow[]): number | false {
  return rows.some(readStillRunning) ? INTAKE_POLL_MS : false
}

/**
 * Every read on this case has finished moving, and at least one produced pages.
 *
 * **The one door reads a bundle, not a complaint.** The fresh-complaint flow
 * had exactly one paper, so the whole screen followed `live[0]` — and pointed
 * at a case file of twelve papers it followed the first and drafted the moment
 * that one landed, with eleven still reading. The extraction then ran against a
 * fraction of the record and every field it could not find came back as a blank
 * the officer would have had to type.
 *
 * A paper the pipeline cannot open at all is not waited for — its read is never
 * coming, and `readStillRunning` already says so — but it does not make the
 * bundle landed either. One paper must have produced pages, or there is nothing
 * to draft from and the caller should still be waiting.
 */
export function allReadsLanded(rows: readonly ReadRow[]): boolean {
  if (rows.length === 0) return false
  if (rows.some(readStillRunning)) return false
  return rows.some(readLanded)
}

/** What the screen tells the officer while the bundle is read: true counts. */
export function bundleReadProgress(rows: readonly ReadRow[]): {
  total: number
  landed: number
  running: number
  failed: number
} {
  return {
    total: rows.length,
    landed: rows.filter(readLanded).length,
    running: rows.filter(readStillRunning).length,
    // A paper with no job is unreadable rather than failed, and it is counted
    // here so "3 of 12" never quietly means "3, and nine we gave up on".
    failed: rows.filter((r) => !readStillRunning(r) && !readLanded(r)).length,
  }
}

/**
 * **Phase A: the confirm screen polls only while something is on its way.**
 *
 * The auto-draft and its gate are gone with the two intake calls they fired
 * (docs/24 item 2): the confirm screen is built on the server from the one
 * reading (`GET /cases/{id}/intake`), and nothing behind that call spends
 * anything, so the screen asks again on a timer only while the server says a
 * paper's text or reading is still moving — and stops the moment it is not.
 */
export function intakePollInterval(
  view: { reading: string } | null | undefined,
): number | false {
  if (!view) return INTAKE_POLL_MS
  return view.reading === 'reading' ? INTAKE_POLL_MS : false
}

/** What `askAgainWanted` needs of one of his four lines — the generated
 *  shape, narrowed, never a hand-written copy of it. */
export type AskableLine = Pick<
  IntakeParticularView,
  'field' | 'status' | 'can_ask_again' | 'asked'
>

/**
 * **One small call, once, for a line the reading left empty** (docs/24 item 3).
 *
 * The server offers it (`can_ask_again`) only for a line with no value and
 * nothing near one on the page, and remembers the asking on the reading, so it
 * is never paid for twice. This is the screen's half of the same promise: fire
 * once per line per screen, never for a line already asked here, and never for
 * one the server says was asked already — a refetch on focus must not re-ask
 * (the twenty translation calls of 32.5 were exactly that).
 */
export function askAgainWanted(line: AskableLine, askedHere: ReadonlySet<string>): boolean {
  return (
    line.status === 'missing' &&
    Boolean(line.can_ask_again) &&
    !line.asked &&
    !askedHere.has(line.field)
  )
}

/* ------------------------------------------------- what Start sends, per door */

/** What the officer chose on the first screen of *Add a case*. */
export type IntakeDoor = 'new' | 'paper' | 'court'

export interface StartChoice {
  door: IntakeDoor
  section: string
  subject: string
  /** The document the officer named — a case on paper, or one at court. */
  reached: string
  /** Off the folder cover. A file at court is asked for none of them. */
  serial: string
  year: string
  openedOn: string
  note: string
}

/** The account a case on paper gives of itself when the officer typed none.
 *  `IC-HIST-01` refuses a case begun in the past that says nothing. */
export const PAPER_NOTE = 'Entered from the paper file.'
export const COURT_NOTE = 'Entered from the papers of the court case.'

/**
 * The one request behind **Start**, for whichever door was chosen.
 *
 * - A new complaint sends nothing but the register it belongs to: the server
 *   numbers it at Confirm and dates it by the organisation's clock.
 * - A case already on paper sends what its folder cover says. The year and the
 *   opening date are the folder's, and the server refuses to guess them.
 * - **A file at court sends no folder cover at all** (Archit, 6 Oct 2026): no
 *   serial, no year, no date. It is a case on paper to every check — the same
 *   door on the server — and the server dates its entry today because nothing
 *   was typed off a folder for it. This function never sends a date of its
 *   own making: the browser's clock is not the register's.
 */
export function startBody(choice: StartChoice): StartIntakeBody {
  const file = { section: choice.section, subject: choice.subject }
  if (choice.door === 'new') {
    return {
      preflight: false,
      historical_entry: false,
      unnumbered_file: false,
      file_number: { ...file, serial: null },
    }
  }
  if (choice.door === 'court') {
    return {
      preflight: false,
      historical_entry: true,
      // No number is typed for it, so the register gives the next, at Confirm.
      unnumbered_file: true,
      declared_reached: choice.reached,
      historical_note: COURT_NOTE,
      file_number: { ...file, serial: null },
    }
  }
  const serial = choice.serial.trim()
  return {
    preflight: false,
    historical_entry: true,
    // Leaving the serial empty, under a field that says the register will
    // give the next, IS the declaration.
    unnumbered_file: !serial,
    declared_reached: choice.reached,
    opened_on: choice.openedOn,
    historical_note: choice.note.trim() || PAPER_NOTE,
    // Phase A: no number is taken here. A folder's own serial is checked now
    // and recorded at Confirm; a blank one is the register's next, taken at
    // Confirm too.
    file_number: { ...file, serial: serial ? Number(serial) : null, year: Number(choice.year) },
  }
}

/** Whether Start may be pressed: what each door still needs said. */
export function startIncomplete(choice: Omit<StartChoice, 'door'> & { door: IntakeDoor | '' }) {
  if (choice.door === '') return true
  if (choice.door === 'new') return false
  if (choice.door === 'court') return !choice.reached
  return !choice.reached || !choice.year || !choice.openedOn
}
